
import Vue from 'vue';
import { cloneDeep } from 'lodash';

import { max, sub, parseISO, isAfter, compareAsc } from 'date-fns';
import { mapActions } from 'vuex';

import { CotationHistorique } from '@/model/cotation-historique';
import { Nullable } from '@/types/types';
import { parseDuration, Duration } from '@/utils/duration';

function parseDate (date: string | number | Date): Date {
  if (typeof date === 'string') {
    return parseISO(date);
  } else if (typeof date === 'number') {
    return new Date(date);
  } else {
    return date;
  }
}

const apexChartLocales = [{
  name: 'fr',
  options: {
    months: ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Decembre'],
    shortMonths: ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Dec'],
    days: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
    shortDays: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'sam'],
    toolbar: {
      download: 'télécharger SVG',
      selection: 'Selection',
      selectionZoom: 'Selection Zoom',
      zoomIn: 'Zoom avant',
      zoomOut: 'Zoom arriere',
      pan: 'Panning',
      reset: 'Zoom par défaut'
    }
  }
}];

type DataItem = {
  x: string | number | Date;
  y: number | null | undefined;
}

type Series = { name?: string, data: DataItem[] }[]

export default Vue.extend({
  name: 'Historique',
  props: {
    idArticle: {
      type: Number,
      required: true
    },
    height: {
      type: Number,
      default: 500
    },
    legend: {
      type: String,
      default: null
    }
  },
  data: () => ({
    charts: {
      options: {
        chart: {
          id: 'main',
          toolbar: {
            show: false
          },
          defaultLocale: 'fr',
          locales: apexChartLocales
        },
        xaxis: {
          type: 'datetime',
          label: {
            datetimeUTC: false
          },
          tooltip: {
            format: 'dd MMM yyyy HH:mm'
          }
        },
        yaxis: {
          labels: {
            formatter: (value: number) => {
              return value.toFixed(0);
            }
          }
        },
        colors: ['#008FFB', '#008FFB'],
        stroke: {
          width: [4, 2],
          curve: 'straight',
          dashArray: [0, 5]
        },
        dataLabels: {
          enabled: false
        },
        legend: {
          show: false
        },
        tooltip: {
          x: {
            format: 'dd MMM yyyy HH:mm'
          },
          y: [
            {
              formatter: (val: number) => val + ' €/t',
              title: {
                formatter: () => ''
              }
            },
            {
              formatter: () => null as null
            }
          ]
        }
      },
      optionsNav: {
        chart: {
          id: 'nav',
          defaultLocale: 'fr',
          locales: apexChartLocales,
          brush: {
            target: 'main',
            enabled: true
          },
          selection: {
            enabled: true,
            xaxis: {
              min: null as Nullable<number>,
              max: null as Nullable<number>
            }
          }
        },

        xaxis: {
          type: 'datetime'
        },
        yaxis: {
          labels: {
            formatter: (value: number) => {
              return value.toFixed(0);
            }
          }
        },
        stroke: {
          curve: 'straight'
        },
        dataLabels: {
          enabled: false
        }
      }
    },
    data: [] as DataItem[],
    mainSeries: [] as Series,
    navSeries: [] as Series,
    loading: true
  }),
  computed: {
    zoomOptions (): any {
      if (!(this.data && this.data.length)) {
        return [];
      }

      const durations = [
        { value: 'P1M', libelle: '1 mois' },
        { value: 'P3M', libelle: '3 mois' },
        { value: 'P6M', libelle: '6 mois' },
        { value: 'P1Y', libelle: '1 an' },
        { value: 'P3Y', libelle: '3 ans' },
        { value: 'P5Y', libelle: '5 ans' }
      ];

      const dateMax = parseDate(this.data[this.data.length - 1].x);
      const dateMin = parseDate(this.data[0].x);

      const options = [];

      for (const d of durations) {
        const duration = parseDuration(d.value) as Duration;
        if (isAfter(sub(dateMax, duration), dateMin)) {
          options.push(d);
        }
      }

      options.push({ value: 'ALL', libelle: 'Tout' });

      return options;
    },
    variation (): number {
      return Math.round(this.data[this.data.length - 1].y - this.data[this.data.length - 2].y);
    },
    variationColor (): string {
      if (this.variation > 0) {
        return 'error';
      } else if (this.variation < 0) {
        return 'primary';
      } else {
        return 'warning';
      }
    },
    variationIcon (): string {
      if (this.variation > 0) {
        return 'arrow-top-right';
      } else if (this.variation < 0) {
        return 'arrow-bottom-right';
      } else {
        return 'arrow-right';
      }
    }
  },
  watch: {
    idArticle () {
      this.init();
    }
  },
  created () {
    this.init();
  },
  methods: {
    ...mapActions({ loadHistorique: 'cotations/loadHistorique' }),
    init () {
      this.loading = true;
      this.loadHistorique({ idArticle: this.idArticle })
        .then((cotations: CotationHistorique[]) => {
          if (cotations.length > 0) {
            const data = this.buildData(cotations);
            const missingData = this.buildMissingData(data);

            this.data = data;
            this.mainSeries = this.buildMainSeries(data, missingData);
            this.navSeries = this.buildNavSeries(data);

            this.zoom('P2Y');
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    buildData (cotations: CotationHistorique[]): DataItem[] {
      return cotations.map(c => ({ x: c.datePublication, y: c.valeur !== undefined ? c.valeur : null })).sort((a, b) => compareAsc(a.x, b.x));
      // return json.map(v => ({ x: v.date, y: v.value }));
    },
    buildMissingData (data: DataItem[]): DataItem[] {
      const missingData: DataItem[] = [];

      let valeurPrec: DataItem = cloneDeep(data[0]);
      let emptyValue = false;

      for (const d of data) {
        if (d.y === null || d.y === undefined) {
          emptyValue = true;
          continue;
        } else {
          if (emptyValue) {
            if (valeurPrec.y === null || valeurPrec.y === undefined) {
              // si les premieres valeur sont nulles
              valeurPrec.y = d.y;
            }
            missingData.push(valeurPrec);
            missingData.push(d);
            missingData.push({ x: d.x, y: null });
          }

          emptyValue = false;
          valeurPrec = d;
        }
      }

      if (emptyValue) {
        // dernieres valeurs nulles
        missingData.push(valeurPrec);
        const v = cloneDeep(valeurPrec);
        v.x = data[data.length - 1].x;
        missingData.push(v);
      }

      return missingData;
    },
    buildMainSeries (data: DataItem[], missingData: DataItem[]): Series {
      return [
        { name: 'Valeur', data },
        { name: '', data: missingData }

      ];
    },
    buildNavSeries (data: DataItem[]): Series {
      return [{ data }];
    },
    zoom (val: string) {
      const duration = parseDuration(val);
      if (duration) {
        const dateMax = parseDate(this.data[this.data.length - 1].x);
        let dateMin = sub(dateMax, duration);

        dateMin = max([dateMin, parseDate(this.data[0].x)]);

        this.charts.optionsNav.chart.selection.xaxis = {
          min: dateMin.getTime(),
          max: dateMax.getTime()
        };
      } else {
        this.charts.optionsNav.chart.selection.xaxis = {
          min: parseDate(this.data[0].x).getTime(),
          max: parseDate(this.data[this.data.length - 1].x).getTime()
        };
      }
      this.charts.optionsNav = cloneDeep(this.charts.optionsNav);
      // this.$forceUpdate();
    }
  }
});
